import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2"; // Install: npm install react-chartjs-2 chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const salesData = [
  {
    SNo: 1,
    customerName: "Rajesh Kumar",
    phone: "9876543210",
    date: "2025-12-13",
    amount: 120,
    product: "Chicken Biryani",
  },
  {
    SNo: 2,
    customerName: "Priya Sharma",
    phone: "8765432109",
    date: "2025-12-13",
    amount: 150,
    product: "Chicken 65",
  },
  {
    SNo: 3,
    customerName: "Amit Patel",
    phone: "7654321098",
    date: "2025-12-12",
    amount: 200,
    product: "Chicken Biryani",
  },
  {
    SNo: 4,
    customerName: "Neha Gupta",
    phone: "6543210987",
    date: "2025-12-11",
    amount: 90,
    product: "Grilled Chicken",
  },
  {
    SNo: 5,
    customerName: "Vikram Singh",
    phone: "5432109876",
    date: "2025-12-10",
    amount: 300,
    product: "Chicken Biryani",
  },
  {
    SNo: 6,
    customerName: "Anita Desai",
    phone: "4321098765",
    date: "2025-12-09",
    amount: 400,
    product: "Butter Chicken",
  },
  {
    SNo: 7,
    customerName: "Suresh Reddy",
    phone: "3210987654",
    date: "2025-12-08",
    amount: 250,
    product: "Chicken 65",
  },
];

export default function Home() {
  const [stats, setStats] = useState({ totalSales: 0, totalOrders: 0 });
  const [topProducts, setTopProducts] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);

  useEffect(() => {
    const totalSales = salesData.reduce((sum, sale) => sum + sale.amount, 0);
    const totalOrders = salesData.length;

    // Most sold products
    const productCount = salesData.reduce((acc, sale) => {
      acc[sale.product] = (acc[sale.product] || 0) + 1;
      return acc;
    }, {});
    const sortedProducts = Object.entries(productCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([product, count]) => ({ product, count }));

    // Highest billed customers
    const customerTotal = salesData.reduce((acc, sale) => {
      acc[sale.customerName] = (acc[sale.customerName] || 0) + sale.amount;
      return acc;
    }, {});
    const sortedCustomers = Object.entries(customerTotal)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([name, amount]) => ({
        name,
        amount,
        phone: salesData.find((s) => s.customerName === name)?.phone,
      }));

    setStats({ totalSales, totalOrders });
    setTopProducts(sortedProducts);
    setTopCustomers(sortedCustomers);
  }, []);

  const chartData = {
    labels: [
      "Dec 4",
      "Dec 5",
      "Dec 6",
      "Dec 7",
      "Dec 8",
      "Dec 9",
      "Dec 10",
      "Dec 11",
      "Dec 12",
      "Dec 13",
    ],
    datasets: [
      {
        label: "Weekly Sales (₹)",
        data: [500, 300, 220, 180, 250, 400, 90, 200, 150, 120],
        borderColor: "rgb(78, 205, 196)",
        backgroundColor: "rgba(78, 205, 196, 0.1)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  return (
    <div className="home-container">
      <div className="home-header">
        <h1 className="gradient-text animate-pulse">
          🏠 FiveStarChicken Dashboard
        </h1>
        <p className="welcome-text slide-in-up">
          Welcome back! Here's what's happening today
        </p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card slide-in-up">
          <div className="stat-icon">💰</div>
          <div className="stat-number">
            ₹{stats.totalSales.toLocaleString()}
          </div>
          <div className="stat-label">Total Revenue</div>
        </div>
        <div className="stat-card slide-in-up delay-100">
          <div className="stat-icon">📦</div>
          <div className="stat-number">{stats.totalOrders}</div>
          <div className="stat-label">Total Orders</div>
        </div>
        <div className="stat-card slide-in-up delay-200">
          <div className="stat-icon">⭐</div>
          <div className="stat-number">4.8</div>
          <div className="stat-label">Avg Rating</div>
        </div>
        <div className="stat-card slide-in-up delay-300">
          <div className="stat-icon">👥</div>
          <div className="stat-number">{salesData.length}</div>
          <div className="stat-label">Active Customers</div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Weekly Sales Chart */}
        <div className="chart-card fade-in">
          <h3>📈 Weekly Sales Trend</h3>
          <div className="chart-container">
            <Line
              data={chartData}
              options={{ responsive: true, maintainAspectRatio: false }}
            />
          </div>
        </div>

        <div className="dual-tables fade-in delay-100">
          {/* Products Table */}
          <div className="side-table products-table">
            <div className="compact-header">
              <h4>🔥 Top Products</h4>
            </div>
            <table className="mini-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Sales</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.slice(0, 5).map((product, index) => (
                  <tr key={product.product}>
                    <td className="rank-mini">{index + 1}</td>
                    <td className="product-cell">{product.product}</td>
                    <td>
                      <span className="mini-badge">{product.count}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Customers Table */}
          <div className="side-table customers-table">
            <div className="compact-header">
              <h4>👑 Top Customers</h4>
            </div>
            <table className="mini-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>₹</th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.slice(0, 5).map((customer, index) => (
                  <tr
                    className={`mini-row ${
                      index < 3 ? `medal-${index + 1}` : ""
                    }`}
                  >
                    <td className="rank-mini">{index + 1}</td>
                    <td className="name-cell">{customer.name}</td>
                    <td>₹{customer.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions fade-in delay-300">
        <h3>Quick Actions</h3>
        <div className="action-buttons">
          <button className="action-btn primary">➕ New Order</button>
          <button className="action-btn secondary">📋 View Inventory</button>
          <button className="action-btn secondary">📊 Full Reports</button>
        </div>
      </div>
    </div>
  );
}
